import React, { useState } from 'react'
import { Form, Row, Col, Input, Typography, Select, Button, Result, Space, notification, Spin } from 'antd'

import FundingService from '../../service/FundingService'
import SimplePaymentService from '../../service/SimplePaymentService'
import { FundingCreatePayload, FundingRetrieve } from '../../domain/Funding'
import { SimplePaymentRetrieve } from '../../domain/SimplePayment'
import { ModeContext } from '../../provider/ModeProvider'
import { useNavigate } from 'react-router-dom'
import CurrencyService from '../../service/CurrencyService'

const { Title } = Typography

const currencies: string[] = CurrencyService.allCurrencies
const most_used_currencies: string[] = CurrencyService.mostUsedCurrencies

const FundingCreateForm: React.FC = () => {
    const { setFundingMode } = ModeContext()
    const [form] = Form.useForm()
    const [transaction, setTransaction] = useState<string | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [refTitle, setRefTitle] = useState<string>('Reference')
    const [simplePaymentOptions, setSimplePaymentOptions] = useState<{ label: string; value: string }[]>([])
    const [fetchingSP, setFetchingSP] = useState(false)
    const [apiNotification, contextNotification] = notification.useNotification()

    const searchedIndex: { [key: string]: boolean } = {}
    const navigate = useNavigate()
    const redirect = (url: string) => {
        navigate(url)
        setFundingMode('')
    }

    const onFinish = () => {
        const values: FundingCreatePayload = form.getFieldsValue() as FundingCreatePayload
        setLoading(true)
        FundingService.create(values)
            .then((result: FundingRetrieve) => {
                setTransaction(result.id)
            })
            .catch((e) => {
                apiNotification.open({
                    message: 'Simulate Funding Failed',
                    description: 'Something went wrong. Please try again later. If the issue keep happening, please contact Customer Support',
                    placement: 'top',
                })
            })
            .finally(() => {
                setSimplePaymentOptions([])
                setLoading(false)
            })
    }

    const createFunding = () => {
        reset()
        setTransaction(null)
    }

    const reset = () => {
        form.resetFields()
    }

    const onSearch = (payload: string) => {
        if (payload.length > 5) {
            setFetchingSP(true)
            SimplePaymentService.retrieve(payload)
                .then((data: SimplePaymentRetrieve) => {
                    if (!searchedIndex[data.id]) {
                        setSimplePaymentOptions([{ label: data.id, value: data.wallexRef }, ...simplePaymentOptions])
                        searchedIndex[data.id] = true
                    }
                })
                .catch(() => {})
                .finally(() => {
                    setFetchingSP(false)
                })
        }
    }

    const prefixSelector = (
        <Form.Item
            name="currencyId"
            rules={[
                {
                    required: true,
                    message: 'Please input Deposit Currency',
                },
            ]}
            noStyle
        >
            <Select
                placeholder="Currency"
                options={[
                    {
                        label: 'Most Used',
                        options: most_used_currencies.map((currency) => ({ label: currency, value: currency })),
                    },
                    {
                        label: 'Others',
                        options: currencies.map((currency) => ({ label: currency, value: currency })),
                    },
                ]}
            ></Select>
        </Form.Item>
    )

    const fundingMethodOptions = [
        { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
        { value: 'COLLECTION', label: 'Collection' },
        { value: 'REFUND', label: 'Refund' },
    ]

    return (
        <>
            {contextNotification}
            {transaction && (
                <Result
                    status="success"
                    title="Deposit Success"
                    subTitle={`Your Funding ID is ${transaction}`}
                    extra={[
                        <Button type="primary" key="console" onClick={createFunding}>
                            Create Another Funding
                        </Button>,
                        <Button htmlType="submit" onClick={() => redirect(`/funding?id=${transaction}`)}>
                            Update Funding Status
                        </Button>,
                    ]}
                />
            )}

            {!transaction && (
                <Form name="collection-create" layout="vertical" onFinish={onFinish} form={form}>
                    <Row>
                        <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 8 }}>
                            <Title level={5}>Funding Information</Title>
                            <Form.Item
                                label="Account ID"
                                name="accountId"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input Account Id',
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="amount"
                                label="Funding Amount"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input Funding Amount',
                                    },
                                ]}
                            >
                                <Input addonAfter={prefixSelector} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item
                                name="fundingMethod"
                                label="Funding Method"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input funding method',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Funding Method"
                                    options={fundingMethodOptions}
                                    onChange={(e) => (e === 'REFUND' ? setRefTitle('Simple Payment ID') : setRefTitle('Reference'))}
                                ></Select>
                            </Form.Item>
                            <Form.Item
                                label={refTitle}
                                name="reference"
                                rules={[
                                    {
                                        required: true,
                                        message: `Please input ${refTitle}`,
                                    },
                                ]}
                            >
                                {refTitle === 'Reference' ? (
                                    <Input />
                                ) : (
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        options={simplePaymentOptions}
                                        placeholder="Input Simple Payment ID"
                                        onSearch={onSearch}
                                        optionFilterProp="label"
                                        notFoundContent={fetchingSP ? <Spin size="small" /> : null}
                                    ></Select>
                                )}
                            </Form.Item>
                            <Form.Item label=" " colon={false}>
                                <Space>
                                    <Button type="primary" htmlType="submit" loading={loading}>
                                        Submit
                                    </Button>
                                    <Button htmlType="submit" onClick={reset}>
                                        Reset
                                    </Button>
                                    <Button htmlType="submit" onClick={() => setFundingMode('')}>
                                        Back
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            )}
        </>
    )
}

export default FundingCreateForm
